import { Injectable, NgZone, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, filter, Observable, Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { PASSIVE_REQUEST, USER_TRIGGERED_REQUEST } from './http-request-context';
import { SocketService } from './socket.service';

interface NotificationPayload {
  id: string;
  type: string;
  title: string;
  message: string;
  createdAt: string;
}

const RESTART_SNACKBAR_CONFIG: Record<string, { key: string; panelClass: string; durationMs: number }> = {
  session_restarted: {
    key: 'notification.session_restarted',
    panelClass: 'notification-snackbar-success',
    durationMs: 6000,
  },
  session_restart_failed: {
    key: 'notification.session_restart_failed',
    panelClass: 'notification-snackbar-error',
    durationMs: 8000,
  },
};

const SHOWN_NOTIFICATION_IDS_LIMIT = 20;

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly socketService = inject(SocketService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly ngZone = inject(NgZone);
  private readonly unreadSubject = new BehaviorSubject<number>(0);
  readonly unreadCount$: Observable<number> = this.unreadSubject.asObservable();

  private pollingInterval: ReturnType<typeof setInterval> | null = null;
  private currentToken: string | null = null;
  private socketSubscriptions = new Subscription();
  private navigationSubscription: Subscription | null = null;
  private shownNotificationIds = new Set<string>();

  private refreshUnreadCount(): void {
    if (this.currentToken) {
      this.fetchUnreadCount(this.currentToken);
    }
  }

  public start(token: string): void {
    this.currentToken = token;
    this.startPolling(token);
    this.setupSocketListeners();
    this.setupNavigationMarkRead();
  }

  public stop(): void {
    this.currentToken = null;
    this.stopPolling();
    this.teardownListeners();
    this.shownNotificationIds.clear();
    this.unreadSubject.next(0);
  }

  private startPolling(token: string): void {
    this.stopPolling();
    this.fetchUnreadCount(token);
    this.pollingInterval = setInterval(() => {
      this.fetchUnreadCount(token);
    }, 60_000);
  }

  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  private setupSocketListeners(): void {
    this.socketSubscriptions.unsubscribe();
    this.socketSubscriptions = new Subscription();

    try {
      this.socketSubscriptions.add(
        this.socketService.on<{ count: number }>('notification:count').subscribe((data) => {
          this.unreadSubject.next(data.count);
        })
      );

      this.socketSubscriptions.add(
        this.socketService
          .on<NotificationPayload>('notification:new')
          .subscribe((payload) => {
            this.refreshUnreadCount();
            this.showNotificationSnackbar(payload);
          })
      );

      this.socketSubscriptions.add(
        this.socketService.on<void>('session:ready').subscribe(() => {
          this.refreshUnreadCount();
        })
      );

      this.socketSubscriptions.add(
        this.socketService.on<void>('session:warning').subscribe(() => {
          this.refreshUnreadCount();
        })
      );
    } catch {
      // Socket not connected yet — polling will catch updates
    }
  }

  private setupNavigationMarkRead(): void {
    this.navigationSubscription?.unsubscribe();
    this.navigationSubscription = this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(() => {
      if (this.currentToken) {
        this.markAsRead(this.currentToken, false);
      }
    });
  }

  private teardownListeners(): void {
    this.socketSubscriptions.unsubscribe();
    this.socketSubscriptions = new Subscription();
    this.navigationSubscription?.unsubscribe();
    this.navigationSubscription = null;
  }

  private showNotificationSnackbar(payload: NotificationPayload): void {
    const config = RESTART_SNACKBAR_CONFIG[payload.type];
    if (!config) {
      return;
    }

    if (this.shownNotificationIds.has(payload.id)) {
      return;
    }

    this.shownNotificationIds.add(payload.id);
    if (this.shownNotificationIds.size > SHOWN_NOTIFICATION_IDS_LIMIT) {
      const oldest = this.shownNotificationIds.values().next().value;
      if (oldest !== undefined) {
        this.shownNotificationIds.delete(oldest);
      }
    }

    const translated = this.translate.instant(config.key);
    const message = translated !== config.key ? translated : payload.title || payload.message;

    this.ngZone.run(() => {
      this.snackBar.open(message, undefined, {
        duration: config.durationMs,
        panelClass: config.panelClass,
      });
    });
  }

  private fetchUnreadCount(token: string): void {
    this.http
      .get<{ unread: number }>(`${environment.apiUrl}/notifications/unread-count`, {
        headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
        context: new HttpContext().set(PASSIVE_REQUEST, true),
      })
      .subscribe({
        next: (data) => this.unreadSubject.next(data.unread),
        error: () => {},
      });
  }

  public markAsRead(token: string, userTriggered = true): void {
    this.unreadSubject.next(0);

    this.buildMarkAllReadRequest(token, userTriggered).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  public markAllAsRead(token: string): Observable<unknown> {
    this.unreadSubject.next(0);

    return this.buildMarkAllReadRequest(token, true);
  }

  private buildMarkAllReadRequest(token: string, userTriggered: boolean): Observable<unknown> {
    const context = userTriggered ? new HttpContext().set(USER_TRIGGERED_REQUEST, true) : undefined;

    return this.http.post(`${environment.apiUrl}/notifications/read`, null, {
      headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
      context,
    });
  }

  public getBellNavigationPath(isOwner: boolean): string {
    return isOwner ? '/admin/alerts' : '/notifications';
  }
}
